import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  ALL_MODULES,
  AppContext,
  EventEmitter,
  getCurrentUserHook,
  isAuthorised,
  LeftNavPane,
  useCurrentServicePortal,
  useIsAuthorisedHook
} from '@nile/components';
import {
  AccountClient,
  GET_PERSONALIZATION_BY_NAME,
  SAVE_PERSONALIZATION,
  TicketClient
} from '@nile/graphql';
import { GET_TASK_VIEW_COUNT } from '@nile/graphql/src/Query/task';
import { GET_TICKET_VIEW_COUNT } from '@nile/graphql/src/Query/ticket';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import { getEmoji } from '@nile/mangifera/src/Pebbles/Views';
import { GET_PROJECT_VIEW_COUNT } from '@nile/projects/src/Graphql/Query/projects';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { clone, cloneDeep } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';
import shortid from 'shortid';
import { CreateTaskView } from '@nile/mangifera/src/Components/Task/TaskView';
import CreateProjectView from '@nile/projects/src/Components/ProjectView/CreateProjectView';
import MonicaAIWorkspaceViews from './MonicaAIWorkspaceViews.tsx';

function NavigationContainer(props) {
  const context = useContext(TicketContext);
  const appContext = useContext(AppContext);
  const { user } = appContext;
  const roleFeatures = user?.role?.roleFeatureList || [];
  const isProjectAuthorised = isAuthorised(roleFeatures, 'project.view');
  // tickets
  const [ticketViewCountMap, setTicketViewCountMap] = useState({});
  const [ticketViewCountLoadingMap, setTicketViewCountLoadingMap] = useState(
    {}
  );
  const ticketLoadingMapRef = useRef(null);
  ticketLoadingMapRef.current = ticketViewCountLoadingMap;
  // project
  const [projectViewCountMap, setProjectViewCountMap] = useState({});
  const [projectViewCountLoadingMap, setProjectViewCountLoadingMap] = useState(
    {}
  );
  const projectLoadingMapRef = useRef(null);
  projectLoadingMapRef.current = projectViewCountLoadingMap;
  // task
  const [taskViewCountMap, setTaskViewCountMap] = useState({});
  const [taskViewCountLoadingMap, setTaskViewCountLoadingMap] = useState({});
  const taskLoadingMapRef = useRef(null);
  taskLoadingMapRef.current = taskViewCountLoadingMap;

  const loadingRefs = {
    ticket: ticketLoadingMapRef,
    project: projectLoadingMapRef,
    task: taskLoadingMapRef
  };

  const loadingSetters = {
    ticket: setTicketViewCountLoadingMap,
    project: setProjectViewCountLoadingMap,
    task: setTaskViewCountLoadingMap
  };

  const countSetters = {
    ticket: setTicketViewCountMap,
    project: setProjectViewCountMap,
    task: setTaskViewCountMap
  };

  const countMap = {
    ticket: ticketViewCountMap,
    project: projectViewCountMap,
    task: taskViewCountMap
  };

  useEffect(() => {
    EventEmitter.subscribe('refreshTicketViewCount', refreshTicketViewCount);
    EventEmitter.subscribe('refreshProjectViewCount', refreshProjectViewCount);
    EventEmitter.subscribe('refreshTaskViewCount', refreshTaskViewCount);
    return function cleanup() {
      EventEmitter.unsubscribe('refreshTicketViewCount');
      EventEmitter.unsubscribe('refreshProjectViewCount');
      EventEmitter.unsubscribe('refreshTaskViewCount');
    };
  }, []);

  const setCounts = (viewCounts, module) => {
    const loadingStates = {};
    const viewCount = [...viewCounts].reduce((acc, { viewId, count }) => {
      acc[viewId] = count;
      loadingStates[viewId] = false;
      return acc;
    }, {});
    countSetters[module]({ ...countMap[module], ...viewCount });
    loadingSetters[module]({
      ...loadingRefs[module],
      ...loadingStates
    });
  };

  const [getTicketViewCount, { data: ticketCount }] = useLazyQuery(
    GET_TICKET_VIEW_COUNT,
    {
      client: TicketClient,
      fetchPolicy: 'cache-and-network',
      // onCompleted({ getTicketViewsCount }) {
      //   if (getTicketViewsCount) {
      //     setCounts(getTicketViewsCount, 'ticket');
      //   }
      // },
      onError() {
        setTicketViewCountLoadingMap({});
      }
    }
  );

  useEffect(() => {
    if (ticketCount?.getTicketViewsCount) {
      setCounts(ticketCount?.getTicketViewsCount, 'ticket');
    }
  }, [ticketCount]);

  const [getProjectViewCount, { data: projectCount }] = useLazyQuery(
    GET_PROJECT_VIEW_COUNT,
    {
      client: TicketClient,
      fetchPolicy: 'cache-and-network',
      // onCompleted({ getProjectViewsCount }) {
      //   if (getProjectViewsCount) {
      //     setCounts(getProjectViewsCount, 'project');
      //   }
      // },
      onError() {
        setProjectViewCountLoadingMap({});
      }
    }
  );

  useEffect(() => {
    if (projectCount?.getProjectViewsCount) {
      setCounts(projectCount?.getProjectViewsCount, 'project');
    }
  }, [projectCount]);

  const [getTaskViewCount, { data: taskCount }] = useLazyQuery(
    GET_TASK_VIEW_COUNT,
    {
      client: TicketClient,
      fetchPolicy: 'cache-and-network',
      onError() {
        setTaskViewCountLoadingMap({});
      }
    }
  );

  useEffect(() => {
    if (taskCount?.getTaskViewsCount) {
      setCounts(taskCount?.getTaskViewsCount, 'task');
    }
  }, [taskCount]);

  const countGetters = {
    ticket: getTicketViewCount,
    project: getProjectViewCount,
    task: getTaskViewCount
  };

  const refreshCounts = (viewIds, module) => {
    countGetters[module]({
      variables: {
        viewIds
      }
    });

    const loadingStates = viewIds?.reduce((acc, viewId) => {
      acc[viewId] = true;
      return acc;
    }, {});

    loadingSetters[module]({
      ...loadingRefs[module].current,
      ...loadingStates
    });
  };

  const refreshTicketViewCount = (viewIds) => {
    refreshCounts(viewIds, 'ticket');
  };

  const refreshProjectViewCount = (viewIds) => {
    if (isProjectAuthorised) {
      refreshCounts(viewIds, 'project');
    }
  };

  const refreshTaskViewCount = (viewIds) => {
    refreshCounts(viewIds, 'task');
  };

  const { ticketViews, taskViews, projectViews, flags } = props;

  useEffect(() => {
    if (ticketViews) {
      const ticketViewIds = ticketViews?.map((view) => view.id) || [];
      refreshTicketViewCount(ticketViewIds);
    }
  }, [ticketViews]);

  useEffect(() => {
    if (taskViews) {
      const taskViewIds = taskViews?.map((view) => view.id) || [];
      refreshTaskViewCount(taskViewIds);
    }
  }, [taskViews]);

  useEffect(() => {
    if (projectViews) {
      const projectViewIds = projectViews?.map((view) => view.id) || [];
      refreshProjectViewCount(projectViewIds);
    }
  }, [projectViews]);

  const handleClick = useCallback(
    (item) => {
      EventEmitter.dispatch('switchTicketView', props.match.params.id);
      context.setPersonalization('');
      context.setActiveView(item);
      context.resetTabState();
    },
    [props.match.params.id]
  );
  const mailViews = [
    {
      id: 'mail-view',
      name: 'Unknown emails'
    }
  ];
  return (
    <div>
      <div className="navigation" location="/">
        <NavigateContainer
          {...{
            ticketViews,
            taskViews,
            taskViewCountLoadingMap,
            taskViewCountMap,
            ticketViewCountLoadingMap,
            ticketViewCountMap,
            handleClick,
            projectViews,
            projectViewCountMap,
            projectViewCountLoadingMap
          }}
        />
      </div>
    </div>
  );
}

export default withRouter(withLDConsumer()(NavigationContainer));

const VIEWS = {
  TASK: 'task',
  TICKET: 'ticket',
  PROJECT: 'project'
};

function NavigateContainer({
  ticketViews,
  taskViews,
  taskViewCountLoadingMap,
  taskViewCountMap,
  ticketViewCountLoadingMap,
  ticketViewCountMap,
  handleClick,
  projectViews,
  projectViewCountMap,
  projectViewCountLoadingMap
}) {
  const context = useContext(TicketContext);
  const [isCreateTaskViewModalOpen, toggleCreateTaskViewModal] = useReducer(
    (isOpen) => !isOpen,
    false
  );

  const [isCreateProjectViewModal, toggleCreateProjectViewModal] = useReducer(
    (isOpen) => !isOpen,
    false
  );

  const user = getCurrentUserHook();
  const getDefaultSheetData = useCallback(
    (isChecked) => {
      const addIsChecked = (item) => ({ ...item, isChecked });
      return {
        [VIEWS.TASK]: {
          list: taskViews?.map(addIsChecked) || [],
          heading: <FormattedMessage id="ticket.navigation.header.taskViews" />,
          isChecked
        },
        [VIEWS.TICKET]: {
          list: ticketViews?.map(addIsChecked) || [],
          heading: <FormattedMessage id="ticket.navigation.header.ticket" />,
          isChecked
        },
        [VIEWS.PROJECT]: {
          list: projectViews?.map(addIsChecked) || [],
          heading: <FormattedMessage id="ticket.navigation.header.project" />,
          isChecked
        }
      };
    },
    [projectViews, ticketViews, taskViews]
  );
  const manageViewCategoryName = useCurrentServicePortal('workspace');
  const {
    data: personalizedData,
    loading: personalizationLoading,
    refetch
  } = useQuery(GET_PERSONALIZATION_BY_NAME, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    variables: {
      userId: user.userId,
      category: manageViewCategoryName,
      name: 'ManageViews'
    }
  });

  const sheetData = useMemo(() => {
    if (
      personalizedData?.getPersonalizationByCategoryAndName == null ||
      !personalizedData?.getPersonalizationByCategoryAndName?.value ||
      personalizedData?.getPersonalizationByCategoryAndName?.value === 'null'
    ) {
      return getDefaultSheetData(true);
    }

    const defaultSheetData = getDefaultSheetData();

    const sidebarData = JSON.parse(
      personalizedData?.getPersonalizationByCategoryAndName.value
    );
    Object.values(VIEWS).forEach((key) => {
      sidebarData[key].heading = defaultSheetData[key].heading;

      const sortedSidebarData = sidebarData[key].list.reduce(
        (personalizedviewList, id) => {
          const viewItem = defaultSheetData[key].list.find(
            (item) => item.id === id
          );

          if (viewItem !== undefined) {
            personalizedviewList.push({
              ...viewItem,
              isChecked: true
            });
          }

          return personalizedviewList;
        },
        []
      );
      const remainingViews = defaultSheetData[key].list.filter(
        (item) => !sidebarData[key].list.includes(item.id)
      );

      sidebarData[key].list = [...sortedSidebarData, ...remainingViews];
    });
    return sidebarData;
  }, [personalizedData, getDefaultSheetData]);

  const [
    savePersonalization,
    { loading: personalizationMutationLoading }
  ] = useMutation(SAVE_PERSONALIZATION, {
    client: AccountClient
  });

  const saveSidebarCustomisation = useCallback(
    (value) => {
      return savePersonalization({
        variables: {
          name: 'ManageViews',
          category: manageViewCategoryName,
          user: {
            firstName: user?.firstName,
            userId: user?.userId,
            email: user?.email,
            lastName: user?.lastName
          },
          value: JSON.stringify(value)
        }
      });
    },
    [user, savePersonalization, manageViewCategoryName]
  );

  function onSave(data, toggleSheet) {
    const dataToBeSaved = data.reduce(
      (acc, { section, heading, list, ...rest }) => {
        acc[section] = {
          ...rest,
          list: list.reduce((idList, { id, isChecked }) => {
            if (isChecked) {
              idList.push(id);
            }
            return idList;
          }, [])
        };
        return acc;
      },
      {}
    );

    saveSidebarCustomisation(dataToBeSaved).then(() => {
      refetch().then(toggleSheet);
    });
  }

  const isAIFeaturesAuthorised = useIsAuthorisedHook('aiFeature.allow');

  return (
    <>
      {isCreateTaskViewModalOpen && (
        <CreateTaskView
          onClose={toggleCreateTaskViewModal}
          allViewsData={sheetData}
        />
      )}

      {isCreateProjectViewModal && (
        <CreateProjectView
          onClose={toggleCreateProjectViewModal}
          personalizedData={personalizedData}
          allViewsData={sheetData}
        />
      )}
      <LeftNavPane
        isLoading={personalizationLoading}
        sheetProps={{
          data: sheetData,
          meta: { heading: <FormattedMessage id="leftPane.manageView" /> },
          onSave,
          onSaveLoading: personalizationMutationLoading
        }}
      >
        {isAIFeaturesAuthorised && (
          <MonicaAIWorkspaceViews handleClick={handleClick} />
        )}

        {Object.entries(sheetData)
          .sort((a, b) => a[1].order - b[1].order)
          .map(([section, viewItem]) => {
            const filteredList = viewItem.list.filter((item) => item.isChecked);
            if (section === VIEWS.TASK && filteredList.length) {
              return (
                <LeftNavPane.Section
                  header={
                    <LeftNavPane.SectionHeader
                      text={viewItem.heading}
                      button={
                        <LeftNavPane.CreateButton
                          roleToCheck="taskViews.add"
                          onClick={() => {
                            toggleCreateTaskViewModal();
                          }}
                        />
                      }
                    />
                  }
                >
                  {filteredList.map((item) => (
                    <LeftNavPane.LinkItem
                      emoji={getEmoji({ ...item, section: VIEWS.TASK })}
                      text={item.name}
                      count={taskViewCountMap[item.id]}
                      isCountLoading={taskViewCountLoadingMap[item.id]}
                      to={`/tasks/${item.id}/task-list`}
                      onClick={() => handleClick(item)}
                    />
                  ))}
                </LeftNavPane.Section>
              );
            }

            if (section === VIEWS.TICKET && filteredList.length) {
              return (
                <LeftNavPane.Section
                  header={
                    <LeftNavPane.SectionHeader
                      text={viewItem.heading}
                      button={
                        <LeftNavPane.CreateButton
                          url={`#/ticketView/${shortid.generate()}/new`}
                          roleToCheck="ticketViews.add"
                          onClick={() => {
                            context.openTab({
                              module: 'TICKET_VIEW',
                              data: {
                                type: 'new'
                              }
                            });
                          }}
                        />
                      }
                    />
                  }
                >
                  {filteredList.map((item) => (
                    <LeftNavPane.LinkItem
                      emoji={getEmoji(item)}
                      text={item.name}
                      count={ticketViewCountMap[item.id]}
                      isCountLoading={ticketViewCountLoadingMap[item.id]}
                      to={`/ticket/${item.id}/ticket-list`}
                      onClick={() => handleClick(item)}
                    />
                  ))}
                </LeftNavPane.Section>
              );
            }

            if (section === VIEWS.PROJECT && filteredList.length) {
              return (
                <LeftNavPane.Section
                  header={
                    <LeftNavPane.SectionHeader
                      text={viewItem.heading}
                      button={
                        <LeftNavPane.CreateButton
                          roleToCheck="projectViews.add"
                          onClick={() => {
                            toggleCreateProjectViewModal();
                          }}
                        />
                      }
                    />
                  }
                >
                  {filteredList.map((item) => (
                    <LeftNavPane.LinkItem
                      emoji={getEmoji({ ...item, section: VIEWS.PROJECT })}
                      text={item.name}
                      count={projectViewCountMap[item.id]}
                      isCountLoading={projectViewCountLoadingMap[item.id]}
                      to={`/project/${item.id}/project-list`}
                      onClick={() => handleClick(item)}
                    />
                  ))}
                </LeftNavPane.Section>
              );
            }

            return null;
          })}
      </LeftNavPane>
    </>
  );
}
